import { observabilityService } from './observability.service';
import alertsService, { type Notification } from './alerts.service';
import {
    calculateThreatScore,
    determineThreatLevel,
    calculateBlockRateScore,
    type ThreatLevel,
} from '../utils/analytics';
import api, { type RequestOptions } from './api';

// ============================================
// TYPES
// ============================================

export interface SecurityOverview {
    threatLevel: ThreatLevel;
    threatScore: number;
    edgesWithSecurity: number;
    edgesWithPolicies: number;
    totalEdges: number;
    packetsBlocked: number;
    packetsInspected: number;
    blockRatePercent: number;
    activePolicies: number;
}

export interface DeviceFleetStatus {
    totalDevices: number;
    onlineDevices: number;
    offlineDevices: number;
    avgCpu: number;
    avgMemory: number;
    devices: Array<{
        id: string;
        name: string;
        status: 'online' | 'offline';
        cpu?: number;
        memory?: number;
    }>;
}

export interface NetworkHealthMetrics {
    vpnAvailability: number;
    deviceConnectivity: number;
    securityStatus: number;
    overallHealth: number;
}

// ============================================
// ENTERPRISE DASHBOARD TYPES
// ============================================

export interface ConfigComplianceSummary {
    synced: number;
    pending: number;
    failed: number;
    unknown: number;
    total: number;
    edgesNeedingAttention: Array<{
        id: string;
        name: string;
        status: string;
        deviceType?: 'edge' | 'connector' | 'mtge';
        dirtyReason?: string;
        lastError?: string;
        lastPushedAt?: string;
    }>;
}

export interface TrafficOverviewSummary {
    totalFlows: number;
    totalBytes: number;
    totalPackets: number;
    bandwidthMbps: number;        // total (rx + tx) — kept for back-compat
    rxBandwidthMbps: number;      // inbound to edge WAN (downloads from internet, uploads from clients)
    txBandwidthMbps: number;      // outbound from edge WAN (uploads to internet, downloads to clients)
    flowExportActive: number;
    protocolBreakdown: Array<{
        protocol: string;
        percentage: number;
    }>;
}

// Protocol label → display name. The agent tags IPFIX samples with
// either the numeric proto (1/6/17/...) or a lowercase string ("tcp",
// "udp", ...). Unclassified flows (hairpinned, control-plane) land in
// proto="0" and dst_port="0" — surface those as "Other" so they don't
// look like a broken query.
const PROTOCOL_NAMES: Record<string, string> = {
    '0': 'Other',
    '1': 'ICMP',
    '6': 'TCP',
    '17': 'UDP',
    '47': 'GRE',
    '50': 'ESP',
    '58': 'ICMPv6',
    'icmp': 'ICMP',
    'tcp': 'TCP',
    'udp': 'UDP',
    'gre': 'GRE',
    'esp': 'ESP',
    'icmpv6': 'ICMPv6',
    '': 'Other',
};

export interface FleetHealthSummary {
    totalEdges: number;
    onlineEdges: number;
    offlineEdges: number;
    avgCpu: number;
    avgMemory: number;
    healthScore: number;
    edgeHealthStatus: Array<{
        id: string;
        name: string;
        status: 'healthy' | 'warning' | 'critical' | 'offline';
        cpu?: number;
        memory?: number;
    }>;
}

export interface SecuritySummary {
    aclEnabled: number;
    // Edges with at least one ACL rule (may be absent on older API builds).
    edgesWithPolicies?: number;
    totalPolicies: number;
    packetsPermitted: number;
    packetsBlocked: number;
    blockRate: number;
}

export interface TopApplication {
    name: string;
    category: string;
    bytes: number;
    percentage: number;
}

export interface TopEdgeEntry {
    /** Edge GUID (the edge_id metric label). */
    edgeId: string;
    /** Friendly edge name. The service leaves this unset; the UI layer resolves it. */
    edgeName?: string;
    /** WAN+LAN throughput over the ranking window, in bytes. */
    bytes: number;
    /** Share of the fleet-wide total over the window, 0-100 (unrounded; the UI formats it). */
    percentage: number;
}

export interface RecentActivity {
    id: string;
    type: 'config_sync' | 'alert' | 'status_change';
    title: string;
    message: string;
    timestamp: string;
    severity?: 'info' | 'warning' | 'error' | 'success';
    edgeId?: string;
}

export interface DashboardSummary {
    kpis: {
        edgesOnline: number;
        edgesTotal: number;
        configPending: number;
        configFailed: number;
        activeAlerts: number;
        // Main: mesh topology tunnels; sub-counters: WG Apps, IKEv2 Apps
        vpnTunnels: {
            mesh: number;
            wgApps: number;
            ikev2Apps: number;
        };
        // Count of active edge peerings across every topology type. Matches the
        // Edge Peering page count; distinct from vpnTunnels.mesh.
        activePeerings: number;
        clientVpnSessions: number;
        aclEnabled: number;
    };
    configCompliance: ConfigComplianceSummary;
    fleetHealth: FleetHealthSummary;
    security: SecuritySummary;
    recentActivity: RecentActivity[];
    // Note: trafficOverview and topApplications are now fetched via PromQL
}

// ============================================
// SERVICE FUNCTIONS
// ============================================

/**
 * Get security overview across all edges
 */
async function getSecurityOverview(options?: RequestOptions): Promise<SecurityOverview> {
    try {
        // Get list of registered edges
        const edgesRes = await observabilityService.getRegisteredEdges(options);
        const edges = edgesRes.status === 'success' ? edgesRes.data || [] : [];
        const totalEdges = edges.length;

        if (totalEdges === 0) {
            return {
                threatLevel: 'Low',
                threatScore: 0,
                edgesWithSecurity: 0,
                edgesWithPolicies: 0,
                totalEdges: 0,
                packetsBlocked: 0,
                packetsInspected: 0,
                blockRatePercent: 0,
                activePolicies: 0,
            };
        }

        // Query aggregate security metrics across all edges
        // Using instant queries for current values

        const [pluginEnabledRes, packetsBlockedRes, packetsInspectedRes, policiesRes, edgesWithPoliciesRes] = await Promise.all([
            observabilityService.query('count(edge_security_plugin_enabled == 1)', undefined, options),
            observabilityService.query('sum(edge_security_packets_blocked_total)', undefined, options),
            observabilityService.query('sum(edge_security_packets_inspected_total)', undefined, options),
            observabilityService.query('sum(edge_security_policies_total)', undefined, options),
            // Edges carrying at least one active policy — drives the posture score.
            observabilityService.query('count(edge_security_policies_total > 0)', undefined, options),
        ]);

        const extractValue = (res: any): number => {
            if (res.status === 'success' && res.data?.result?.length > 0) {
                return parseFloat(res.data.result[0].value?.[1] || '0');
            }
            return 0;
        };

        const edgesWithSecurity = extractValue(pluginEnabledRes);
        const packetsBlocked = extractValue(packetsBlockedRes);
        const packetsInspected = extractValue(packetsInspectedRes);
        const activePolicies = extractValue(policiesRes);
        const edgesWithPolicies = extractValue(edgesWithPoliciesRes);

        const blockRatePercent = packetsInspected > 0
            ? (packetsBlocked / packetsInspected) * 100
            : 0;

        // Only block rate is wired to a real metric today. Passing just that
        // lets calculateThreatScore re-normalise so block rate spans the full
        // 0-100 range (previously the stubbed 0s capped the score at 30, making
        // the High/Critical bands unreachable). Add connectionAnomaly /
        // ipDiversity / alertFrequency here as their metrics come online.
        const blockRateScore = calculateBlockRateScore(blockRatePercent);
        const threatScore = calculateThreatScore({ blockRate: blockRateScore });
        const threatLevel = determineThreatLevel(threatScore);

        return {
            threatLevel,
            threatScore,
            edgesWithSecurity,
            edgesWithPolicies,
            totalEdges,
            packetsBlocked,
            packetsInspected,
            blockRatePercent,
            activePolicies,
        };
    } catch (error) {
        console.error('Failed to fetch security overview:', error);
        return {
            threatLevel: 'Low',
            threatScore: 0,
            edgesWithSecurity: 0,
            edgesWithPolicies: 0,
            totalEdges: 0,
            packetsBlocked: 0,
            packetsInspected: 0,
            blockRatePercent: 0,
            activePolicies: 0,
        };
    }
}

/**
 * Get device fleet status summary
 */
async function getDeviceFleetStatus(options?: RequestOptions): Promise<DeviceFleetStatus> {
    try {
        // Get registered edges
        const edgesRes = await observabilityService.getRegisteredEdges(options);
        const edges = edgesRes.status === 'success' ? edgesRes.data || [] : [];

        if (edges.length === 0) {
            return {
                totalDevices: 0,
                onlineDevices: 0,
                offlineDevices: 0,
                avgCpu: 0,
                avgMemory: 0,
                devices: [],
            };
        }

        // Get telemetry for all edges
        const telemetryRes = await observabilityService.getEdgesTelemetry(options);
        const telemetry = telemetryRes.status === 'success' ? telemetryRes.data || {} : {};

        let onlineCount = 0;
        let totalCpu = 0;
        let totalMemory = 0;
        let devicesWithTelemetry = 0;

        const devices = edges.map((edge) => {
            const edgeTelemetry = telemetry[edge.id];
            const isOnline = edgeTelemetry && (edgeTelemetry.cpu !== undefined || edgeTelemetry.uptime !== undefined);

            if (isOnline) {
                onlineCount++;
                if (edgeTelemetry.cpu !== undefined) {
                    totalCpu += edgeTelemetry.cpu;
                    devicesWithTelemetry++;
                }
                if (edgeTelemetry.memory !== undefined) {
                    totalMemory += edgeTelemetry.memory;
                }
            }

            return {
                id: edge.id,
                name: edge.name,
                status: isOnline ? 'online' as const : 'offline' as const,
                cpu: edgeTelemetry?.cpu,
                memory: edgeTelemetry?.memory,
            };
        });

        return {
            totalDevices: edges.length,
            onlineDevices: onlineCount,
            offlineDevices: edges.length - onlineCount,
            avgCpu: devicesWithTelemetry > 0 ? totalCpu / devicesWithTelemetry : 0,
            avgMemory: devicesWithTelemetry > 0 ? totalMemory / devicesWithTelemetry : 0,
            devices,
        };
    } catch (error) {
        console.error('Failed to fetch Edge fleet status:', error);
        return {
            totalDevices: 0,
            onlineDevices: 0,
            offlineDevices: 0,
            avgCpu: 0,
            avgMemory: 0,
            devices: [],
        };
    }
}

/**
 * Get recent notifications (limit 5)
 */
async function getRecentNotifications(): Promise<Notification[]> {
    try {
        const notifications = await alertsService.getNotifications({ limit: 5 });
        return notifications;
    } catch (error) {
        console.error('Failed to fetch recent notifications:', error);
        return [];
    }
}

/**
 * Calculate network health metrics
 */
async function getNetworkHealthMetrics(fleetStatus?: DeviceFleetStatus, options?: RequestOptions): Promise<NetworkHealthMetrics> {
    try {
        // Use provided fleet status or fetch new
        const fleet = fleetStatus || await getDeviceFleetStatus(options);

        // Device connectivity: % of devices online
        const deviceConnectivity = fleet.totalDevices > 0
            ? (fleet.onlineDevices / fleet.totalDevices) * 100
            : 0;

        // VPN Availability: Query VPN tunnel status
        // For now, use device connectivity as proxy (tunnels work if devices are online)
        const vpnAvailability = deviceConnectivity;

        // Security status: Based on security overview
        const security = await getSecurityOverview(options);
        // Invert threat score - lower threat = higher security health
        const securityStatus = 100 - security.threatScore;

        // Overall health: weighted average
        const overallHealth = (deviceConnectivity * 0.4) + (vpnAvailability * 0.3) + (securityStatus * 0.3);

        return {
            vpnAvailability: Math.round(vpnAvailability),
            deviceConnectivity: Math.round(deviceConnectivity),
            securityStatus: Math.round(securityStatus),
            overallHealth: Math.round(overallHealth),
        };
    } catch (error) {
        console.error('Failed to calculate network health:', error);
        return {
            vpnAvailability: 0,
            deviceConnectivity: 0,
            securityStatus: 0,
            overallHealth: 0,
        };
    }
}

// ============================================
// ENTERPRISE DASHBOARD API FUNCTIONS
// ============================================

/**
 * Get complete dashboard summary from API
 * Single call for all dashboard data
 */
async function getDashboardSummary(tenantId?: string, options?: RequestOptions): Promise<DashboardSummary | null> {
    try {
        const params = tenantId ? `?tenantId=${tenantId}` : '';
        const response = await api.get<{ success: boolean; data: DashboardSummary }>(`/dashboard/summary${params}`, options);
        // Note: Axios interceptor already unwraps response.data, so `response` IS the HTTP body
        const body = response as any;
        if (body.success && body.data) {
            return body.data;
        }
        return null;
    } catch (error) {
        console.error('Failed to fetch dashboard summary:', error);
        return null;
    }
}

/**
 * Get config compliance summary
 */
async function getConfigComplianceSummary(tenantId?: string, options?: RequestOptions): Promise<{
    summary: ConfigComplianceSummary;
    edges: Array<{
        id: string;
        name: string;
        status: string;
        isDirty: boolean;
        deviceType?: 'edge' | 'connector' | 'mtge';
        dirtyReason?: string;
        lastError?: string;
        lastPushedAt?: string;
        lastAppliedAt?: string;
    }>;
} | null> {
    try {
        const params = tenantId ? `?tenantId=${tenantId}` : '';
        const response = await api.get<{ success: boolean; data: any }>(`/dashboard/config-compliance${params}`, options);
        // Note: Axios interceptor already unwraps response.data, so `response` IS the HTTP body
        const body = response as any;
        if (body.success && body.data) {
            return body.data;
        }
        return null;
    } catch (error) {
        console.error('Failed to fetch config compliance:', error);
        return null;
    }
}

/**
 * Get traffic overview via PromQL from VictoriaMetrics
 * This fetches IPFIX flow data directly from the metrics store
 */
async function getTrafficOverviewViaPromQL(edgeIds?: string[], options?: RequestOptions): Promise<TrafficOverviewSummary> {
    try {
        // Build edge filter if specific edges provided
        const edgeFilter = edgeIds && edgeIds.length > 0
            ? `{edge_id=~"${edgeIds.join('|')}"}`
            : '';

        // Traffic totals/bandwidth come from the interface counters
        // (edge_interface_*_bytes_total{role="wan|lan"}) — IPFIX
        // counters only see flows captured by flowprobe, which on
        // TAP-bridge cloud VMs is a partial view (service taps, WG
        // tunnels) and always under-reports overall WAN throughput.
        // Flow/packet/protocol breakdown still comes from IPFIX since
        // that's the only source with L4 classification.
        const ifaceFilter = edgeIds && edgeIds.length > 0
            ? `{edge_id=~"${edgeIds.join('|')}", role=~"wan|lan"}`
            : `{role=~"wan|lan"}`;
        // Bandwidth uses rate[5m] for the live rate (matches the analytics
        // page). The previous formula computed increase[1h]/3600, which on
        // low-traffic tenants rounded to 0 Mbps even when the analytics
        // chart showed hundreds of bps (VSNP-663 #1).
        // Totals and packet/flow counts still use increase[1h] because they
        // are *cumulative* over the window, not a rate.
        const [rxRateRes, txRateRes, rxBytesTotalRes, txBytesTotalRes, packetsRes, flowsRes, protocolRes, enabledRes] = await Promise.all([
            observabilityService.query(`sum(rate(edge_interface_rx_bytes_total${ifaceFilter}[5m]))`, undefined, options),
            observabilityService.query(`sum(rate(edge_interface_tx_bytes_total${ifaceFilter}[5m]))`, undefined, options),
            observabilityService.query(`sum(increase(edge_interface_rx_bytes_total${ifaceFilter}[1h]))`, undefined, options),
            observabilityService.query(`sum(increase(edge_interface_tx_bytes_total${ifaceFilter}[1h]))`, undefined, options),
            observabilityService.query(`sum(increase(edge_ipfix_packets_total${edgeFilter}[1h]))`, undefined, options),
            observabilityService.query(`sum(increase(edge_ipfix_flows_total${edgeFilter}[1h]))`, undefined, options),
            observabilityService.query(`sum by (protocol) (increase(edge_ipfix_bytes_total${edgeFilter}[1h]))`, undefined, options),
            observabilityService.query(`count(edge_ipfix_enabled${edgeFilter} == 1)`, undefined, options),
        ]);

        const extractValue = (res: any): number => {
            if (res.status === 'success' && res.data?.result?.length > 0) {
                return parseFloat(res.data.result[0].value?.[1] || '0');
            }
            return 0;
        };

        // 1h totals (cumulative bytes / packets / flows over the window)
        const rxBytes = extractValue(rxBytesTotalRes);
        const txBytes = extractValue(txBytesTotalRes);
        const totalBytes = rxBytes + txBytes;
        const totalPackets = extractValue(packetsRes);
        const totalFlows = extractValue(flowsRes);
        const flowExportActive = extractValue(enabledRes);

        // Live bandwidth (bytes per second from rate[5m] → bits per second → Mbps)
        const rxBytesPerSec = extractValue(rxRateRes);
        const txBytesPerSec = extractValue(txRateRes);
        const rxBandwidthMbps = (rxBytesPerSec * 8) / 1_000_000;
        const txBandwidthMbps = (txBytesPerSec * 8) / 1_000_000;
        const bandwidthMbps = rxBandwidthMbps + txBandwidthMbps;

        // Process protocol breakdown
        const protocolBreakdown: Array<{ protocol: string; percentage: number }> = [];
        if (protocolRes.status === 'success' && protocolRes.data?.result?.length > 0) {
            let totalProtoBytes = 0;
            const protocols: Array<{ protocol: string; bytes: number }> = [];

            for (const result of protocolRes.data.result) {
                const bytes = parseFloat(result.value?.[1] || '0');
                const protoNum = result.metric?.protocol || 'unknown';
                totalProtoBytes += bytes;
                protocols.push({
                    protocol: PROTOCOL_NAMES[String(protoNum).toLowerCase()] || PROTOCOL_NAMES[protoNum] || String(protoNum).toUpperCase(),
                    bytes,
                });
            }

            // Calculate percentages and sort
            for (const proto of protocols) {
                const percentage = totalProtoBytes > 0
                    ? Math.round((proto.bytes / totalProtoBytes) * 100)
                    : 0;
                if (percentage > 0) {
                    protocolBreakdown.push({
                        protocol: proto.protocol,
                        percentage,
                    });
                }
            }
            protocolBreakdown.sort((a, b) => b.percentage - a.percentage);
        }

        return {
            totalFlows: Math.round(totalFlows),
            totalBytes: Math.round(totalBytes),
            totalPackets: Math.round(totalPackets),
            bandwidthMbps: Math.round(bandwidthMbps * 100) / 100,
            rxBandwidthMbps: Math.round(rxBandwidthMbps * 100) / 100,
            txBandwidthMbps: Math.round(txBandwidthMbps * 100) / 100,
            flowExportActive: Math.round(flowExportActive),
            protocolBreakdown,
        };
    } catch (error) {
        console.error('Failed to fetch traffic overview via PromQL:', error);
        return {
            totalFlows: 0,
            totalBytes: 0,
            totalPackets: 0,
            bandwidthMbps: 0,
            rxBandwidthMbps: 0,
            txBandwidthMbps: 0,
            flowExportActive: 0,
            protocolBreakdown: [],
        };
    }
}

// Per-interface throughput ceiling used to clamp counter-wrap corruption:
// on a VPP restart the agent's uint64 byte counters can underflow and yield a
// nonsensically large increase() for one interface. 10 Gbps over the ranking
// window is an impossible-to-exceed real value (edge NICs are 1–2.5 Gbps), so
// clamping there drops the corruption without touching legitimate traffic.
// Mirrors the guard TrafficAnalytics applies to the same counters. (VSNP-966)
const MAX_IFACE_BYTES_PER_SEC = 1_250_000_000; // 10 Gbps
const TOP_EDGES_WINDOW_SEC = 3600; // 1h ranking window

/**
 * Get the busiest edges (by total throughput) via PromQL from VictoriaMetrics.
 *
 * VSNP-966 (spun out of VSNP-663 #6): the fleet-wide dashboard used to rank
 * destination ports (`sum by (dst_port)`), which tells an operator little about
 * *where* traffic is on a multi-edge fleet. We now rank whole edges by their
 * total throughput and let the card drill down into a single edge.
 *
 * Source metric: the interface byte counters
 * (`edge_interface_{rx,tx}_bytes_total{role=~"wan|lan"}`), NOT IPFIX
 * (`edge_ipfix_bytes_total`). IPFIX on TAP-bridge cloud VMs is a partial,
 * under-reporting view (service taps / WG tunnels only) and is empty whenever
 * flowprobe captures no flows — which is why the old Top Ports card rendered
 * nothing. The interface counters are the accurate throughput source and
 * already back the Traffic Overview totals, so summing rx+tx across every
 * WAN/LAN port on an edge is both correct and populated whenever the edge is
 * online. Grouping by `edge_id` ranks the busiest edges.
 *
 * The optional `edgeIds` scope is applied consistently with the sibling IPFIX
 * queries (the old query ignored it — VSNP-663 secondary finding). Tenant
 * scoping is always enforced server-side by the metrics proxy.
 */
async function getTopEdgesViaPromQL(edgeIds?: string[], options?: RequestOptions): Promise<TopEdgeEntry[]> {
    try {
        const labels = edgeIds && edgeIds.length > 0
            ? `edge_id=~"${edgeIds.join('|')}", role=~"wan|lan"`
            : `role=~"wan|lan"`;
        // Union rx+tx via an __name__ regex rather than `increase(rx) + increase(tx)`:
        // the arithmetic form is a 1:1 vector join that silently drops an interface
        // when only one of its two counters has a sample in the window (counter reset,
        // freshly-appeared series). clamp_max caps per-interface counter-wrap corruption
        // (see MAX_IFACE_BYTES_PER_SEC). ONE grouped query returns every edge's total —
        // edge counts are small (tens), so we derive the top-N and the fleet-wide
        // percentage denominator client-side. That avoids a second full-scan `sum()`
        // query and keeps numerator and denominator on one consistent snapshot. (VSNP-966)
        const selector = `{__name__=~"edge_interface_(rx|tx)_bytes_total", ${labels}}`;
        const perIfaceCap = MAX_IFACE_BYTES_PER_SEC * TOP_EDGES_WINDOW_SEC;
        const res = await observabilityService.query(
            `sum by (edge_id) (clamp_max(increase(${selector}[${TOP_EDGES_WINDOW_SEC}s]), ${perIfaceCap}))`,
            undefined,
            options,
        );

        if (res.status !== 'success' || !res.data?.result?.length) {
            return [];
        }

        let fleetTotalBytes = 0;
        const edges: Array<{ edgeId: string; bytes: number }> = [];
        for (const result of res.data.result) {
            const bytes = parseFloat(result.value?.[1] || '0');
            const edgeId = result.metric?.edge_id || '';
            if (bytes > 0 && edgeId) {
                fleetTotalBytes += bytes;
                edges.push({ edgeId, bytes });
            }
        }

        // Rank busiest-first, keep the top 5; percentage is share of the fleet total.
        // Kept UNROUNDED — on a large fleet no single edge is a big share, so rounding
        // to an integer here collapses every row to "0%"/"1%"; the card formats it with
        // adaptive precision and derives bar width from bytes, not this value. (VSNP-966)
        edges.sort((a, b) => b.bytes - a.bytes);
        return edges.slice(0, 5).map((e) => ({
            edgeId: e.edgeId,
            // edgeName is intentionally left unset here — the service has no edge-name
            // catalogue; the UI layer resolves it (falls back to a short id).
            bytes: Math.round(e.bytes),
            percentage: fleetTotalBytes > 0 ? (e.bytes / fleetTotalBytes) * 100 : 0,
        }));
    } catch (error) {
        console.error('Failed to fetch top edges via PromQL:', error);
        return [];
    }
}

// ============================================
// EXPORT SERVICE
// ============================================

export const dashboardSummaryService = {
    // Legacy methods
    getSecurityOverview,
    getDeviceFleetStatus,
    getRecentNotifications,
    getNetworkHealthMetrics,
    // New enterprise dashboard methods
    getDashboardSummary,
    getConfigComplianceSummary,
    // Traffic data via PromQL (not API)
    getTrafficOverviewViaPromQL,
    getTopEdgesViaPromQL,
};
